(ns lupapalvelu.ui.user-groups.application
  "Application-side UI for group-based authority access.

  This component is mounted on an application when the current user can see or manage
  user groups authed to that application. It lists the groups that already have
  application access, lets authorized users disinvite them, and provides the invite bubble
  for selecting one remaining organization group and sending an optional message to its
  members."
  (:require [lupapalvelu.common.hub :as hub]
            [lupapalvelu.next.event :refer [<sub >evt]]
            [lupapalvelu.ui.common :refer [loc]]
            [lupapalvelu.ui.components :as components]
            [lupapalvelu.ui.components.reselect :as reselect]
            [lupapalvelu.ui.dom :as ui-dom]
            [lupapalvelu.ui.user-groups.data :as data]
            [lupapalvelu.ui.user-groups.shared :as shared]
            [reagent.core :as r]))

(defn waiting? []
  (<sub [::data/waiting?]))

(defn can-invite? []
  (<sub [:auth/application? :application-invite-user-group]))

(defn can-disinvite? []
  (<sub [:auth/application? :application-disinvite-user-group]))

(defn user-group-ids []
  (not-empty (<sub [::data/group-ids])))

(defn group-select []
  (when-let [candidates (<sub [::data/field-value :candidates])]
    [:div.flex--column
     [:label.lux.required {:for "group-candidates"} (loc :user-groups.group)]
     [reselect/reselect
      (<sub [::data/field-value :group-id])
      {:items     candidates
       :required? true
       :id        "group-candidates"
       :disabled? (waiting?)
       :class     :w--min-40em
       :callback  #(>evt [::data/edit-field :group-id %])}]]))

(defmethod shared/table-cell :application-actions
  [_ _ group-id _]
  [:td {:style {:padding 0}}
   (when (can-disinvite?)
     [:div.va--top.txt--center
      [shared/remove-button [::data/disinvite-user-group group-id]
       :user-groups.disinvite (<sub [::data/group-name group-id])]])])

(defn group-table []
  (when-let [xs (user-group-ids)]
    (let [columns (cond-> [:name :description]
                    (can-disinvite?)
                    (conj :application-actions))]
      [:table.gap--b0.gap--t1
       {:style {:width         :fit-content
                :margin-bottom 0}}
       [shared/caption :user-groups.application]
       [shared/table-header columns]
       [:tbody
        (for [group-id xs]
          [:tr {:key group-id}
           (for [col columns]
             [:<> {:key col}
              [shared/table-cell :group nil group-id col]])])]])))

(defn invite-button []
  [components/icon-button
   {:text-loc :user-groups.invite
    :icon     :lupicon-circle-plus
    :class    :primary
    :on-click #(>evt [::data/toggle-editing])}])

(defn invite-bubble []
  (r/with-let [_ (>evt [::data/fetch-candidates])]
    [:div.bubble-dialog.flex--column.flex--gap1
     {:style {:width :fit-content}}
     (if-let [error (<sub [::data/error-message])]
       [:div.error-note error]
       [:div.flex--column.flex--gap2
        [group-select]
        (when-let [description (<sub [::data/application-candidate-description])]
          [:div.ws--pre-wrap.pad--h1 description])
        [shared/textfield :message {:lines 5}]
        [:div.flex.flex--gap2
         [components/icon-button {:class     :primary
                                  :text-loc  :application-guests.send
                                  :icon      :lupicon-circle-arrow-right
                                  :disabled? (or (waiting?)
                                                 (not (<sub [::data/field-value :group-id])))
                                  :on-click  #(>evt [::data/invite-user-group])}]
         [:button.secondary {:on-click #(>evt [::data/toggle-editing false])
                             :disabled (waiting?)}
          (loc :cancel)]]])]))

(defn invite-button+bubble []
  (when (can-invite?)
    (let [editing? (<sub [::data/editing?])]
      [:div
       [:div.stacked
        [invite-button]
        (when editing?
          [:div.bubble-dialog-arrow])]
       (when editing?
         [invite-bubble])])))

(defn view []
  (r/with-let [_ (>evt [::data/toggle-visible true])
               _ (>evt [::data/fetch-user-groups])
               _ (>evt [::hub/subscribe "dialog-close"
                        #(>evt [::data/toggle-editing false])
                        ::dialog-close-id])]
    (when (or (user-group-ids) (can-invite?))
      [:div.flex--column.flex--gap2.gap--b2
       [group-table]
       [invite-button+bubble]])
    (finally
      (>evt [::data/toggle-visible false])
      (>evt [::hub/unsubscribe ::dialog-close-id]))))

(defonce mounter (ui-dom/init :reagent (fn [] view)))

(defn ^:export start [dom-id]
  (ui-dom/mount! mounter dom-id))
